import { useRef, useState } from "react";
import Layout from "../components/Layout";
import CountdownTimer from "../components/CountdownTimer";
import ShareButton from "../components/ShareButton";
import DownloadImageButton from "../components/DownloadImageButton";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Play, RotateCcw, Palette, Copy } from "lucide-react";

const COLOR = "#A855F7";
const DURATION = 300;

const Swatch = ({ hex, nombre, delay = 0 }) => (
  <div
    className="flex flex-col items-start"
    style={{ animation: `pop-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay}ms both` }}
  >
    <div
      className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl border-4 border-[#1a1a1a] shadow-hard-sm"
      style={{ background: hex }}
    />
    <div className="mt-3 font-mono text-xs sm:text-sm font-bold text-[#1a1a1a] leading-tight">{nombre}</div>
    <div className="font-mono text-xs text-[#737373] tabular-nums">{hex}</div>
  </div>
);

const SkeletonSwatches = () => (
  <div className="flex gap-4 sm:gap-6 animate-pulse">
    {[0, 1, 2].map((i) => (
      <div key={i} className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl border-4 border-[#1a1a1a] bg-[#e5e1d5]" />
    ))}
  </div>
);

const PaletaMaldita = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("idle");
  const cardRef = useRef(null);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.post("/generate/paleta-maldita", {});
      setData(res.data);
      setPhase("ready");
    } catch {
      toast.error("Ups, algo salió mal. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const start = () => setPhase("running");
  const finish = () => setPhase("done");

  const copyHexes = async () => {
    if (!data?.data?.colores) return;
    try {
      await navigator.clipboard.writeText(data.data.colores.join(" / "));
      toast.success("Hexes copiados");
    } catch {
      toast.error("No se pudo copiar.");
    }
  };

  const d = data?.data || {};
  const colores = d.colores || [];
  const nombres = d.nombres || [];

  return (
    <Layout accent={COLOR}>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16">
        <div className="mb-8">
          <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] font-bold bg-[#A855F7] text-white border-4 border-[#1a1a1a] rounded-full px-4 py-1 mb-4">
            tool · 06 · nuevo
          </span>
          <h1 className="font-display font-black text-5xl sm:text-7xl leading-[0.95] tracking-tight mb-4">
            paleta <span className="text-[#A855F7]">maldita</span>.
          </h1>
          <p className="font-mono text-sm sm:text-base text-[#4a4a4a] max-w-xl">
            3 colores que jamás deberían ir juntos + un reto. Tienes 5 minutos para hacer algo hermoso.
          </p>
        </div>

        {phase === "idle" && !loading && (
          <button
            data-testid="paleta-generate-btn"
            onClick={generate}
            className="press bg-[#A855F7] text-white border-4 border-[#1a1a1a] rounded-2xl px-8 py-5 font-display font-black text-2xl shadow-hard-md inline-flex items-center gap-2"
          >
            <Palette className="w-6 h-6" strokeWidth={3} />
            ¡Maldecir paleta!
          </button>
        )}

        {loading && phase === "idle" && (
          <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-8">
            <SkeletonSwatches />
          </div>
        )}

        {phase === "ready" && data && (
          <div ref={cardRef} className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-lg p-6 sm:p-10 pop-in relative" data-testid="paleta-ready">
            <div className="absolute top-3 right-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373] font-bold">
              guango · paleta maldita
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-5 text-[#737373]">tu paleta</div>
            <div className="flex flex-wrap gap-5 sm:gap-7 mb-8" data-testid="swatches">
              {colores.map((hex, i) => (
                <Swatch key={i} hex={hex} nombre={nombres[i]} delay={i * 150} />
              ))}
            </div>

            <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-2 text-[#737373]">el reto</div>
            <h2 className="font-display font-black text-2xl sm:text-4xl leading-tight mb-3">
              {d.reto}
            </h2>
            <p className="font-mono text-xs text-[#737373] mb-8">
              Regla: solo puedes usar blanco y negro para texto. Nada más.
            </p>

            <div className="flex flex-wrap gap-3" data-exclude-from-image="true">
              <button
                data-testid="paleta-start-btn"
                onClick={start}
                className="press inline-flex items-center gap-2 bg-[#22c55e] border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-display font-black text-xl shadow-hard-md"
              >
                <Play className="w-5 h-5" strokeWidth={3} /> iniciar 5:00
              </button>
              <button
                data-testid="paleta-copy-btn"
                onClick={copyHexes}
                className="press inline-flex items-center gap-2 bg-white border-4 border-[#1a1a1a] rounded-xl px-5 py-3 font-mono text-sm uppercase tracking-wider font-bold shadow-hard-sm"
              >
                <Copy className="w-4 h-4" strokeWidth={3} /> copiar hexes
              </button>
              <button
                data-testid="paleta-regen-btn"
                onClick={generate}
                disabled={loading}
                className="press inline-flex items-center gap-2 bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-xl px-5 py-3 font-mono text-sm uppercase tracking-wider font-bold shadow-hard-sm"
              >
                otra paleta
              </button>
              <ShareButton promptId={data.id} color={COLOR} testId="paleta-share" />
              <DownloadImageButton
                targetRef={cardRef}
                fileName={`guango-paleta-maldita-${data.id.slice(0, 8)}.png`}
                color={COLOR}
                testId="paleta-download"
              />
            </div>
          </div>
        )}

        {phase === "running" && (
          <div data-testid="paleta-running">
            <div className="flex flex-wrap gap-3 mb-6">
              {colores.map((hex, i) => (
                <div key={i} className="flex items-center gap-2 bg-white border-4 border-[#1a1a1a] rounded-xl px-3 py-2 shadow-hard-sm">
                  <div className="w-6 h-6 rounded border-2 border-[#1a1a1a]" style={{ background: hex }} />
                  <span className="font-mono text-xs font-bold tabular-nums">{hex}</span>
                </div>
              ))}
            </div>
            <CountdownTimer duration={DURATION} color={COLOR} running format="minutes" warnAt={60} onComplete={finish} />
          </div>
        )}

        {phase === "done" && (
          <div className="bg-[#A855F7] text-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-lg p-8 sm:p-12 pop-in" data-testid="paleta-done">
            <div className="font-display font-black text-5xl sm:text-7xl mb-4">¡tiempo!</div>
            <p className="font-mono text-base sm:text-lg mb-8">Muestra tu diseño 🎨</p>
            <div className="flex flex-wrap gap-3">
              <button
                data-testid="paleta-another-btn"
                onClick={generate}
                disabled={loading}
                className="press inline-flex items-center gap-2 bg-[#1a1a1a] border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-display font-black text-xl shadow-hard-md"
              >
                <RotateCcw className="w-5 h-5" strokeWidth={3} /> otra paleta
              </button>
              {data && <ShareButton promptId={data.id} color="#fff" testId="paleta-done-share" />}
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default PaletaMaldita;
